import { createSlice } from '@reduxjs/toolkit'
import { saveState } from '../../localStorage'

const initialState = {
    value: {
        propertyClass: "",
        propertyType: 'Affordable Apartments',
        parkingConfig: 'Assigned or Deeded',
        yearBuilt: null,
        units: null,
        parkingSpaces: null,
        assignedParkingSpaces: null,
        parkingSpacesToElectrify: null,
        assignedParkingSpacesToElectrify: null,
        state: "Alabama",
        newDevelopment: false,
        city: null,
        zip: null,
        streetAddress: null,
        role: [],
        customRole: null,
        optIn: false,
        emailAddress: null,
        // Additional Configurations
        annualVehiclesMiles: null,
        avgAtHomeRate: null,
        avgDCFCRate: null,
        benefitToBuilding: null,
        benefitToResident: null,
        discountRate: null,
        evEfficiency: null,
        roiPeriod: null,
        vehiclesPerPrivateSpot: null,
        vehiclesPerSharedSpot: null,
        rowid: null,
        incentives: [],
        roiMonthlyArr: [],
        roiAnnualArr: [],
        costMonthlyArr: [],
        costOneTimeArr: []
    },
    valid: false,
    updateSpacesToElectrify: true,
    advancedConfigToShow: [],
    enableRecalculate: false,
    enableReset: false
}

const inputTypesRequired = {
  propertyClass: ["string", false],
  propertyType: ["string", true],
  parkingConfig: ["string", true],
  yearBuilt: ["number", true],
  units: ["number", true],
  parkingSpaces: ["number", true],
  assignedParkingSpaces: ["number", false],
  parkingSpacesToElectrify: ["number", false],
  assignedParkingSpacesToElectrify: ["number", false],
  state: ["string", true],
  newDevelopment: ["boolean", true],
  city: ["string", true],
  zip: ["string", true],
  streetAddress: ["string", true],
  role: ["object", false],
  customRole: ["string", false],
  optIn: ["boolean", true],
  emailAddress: ["string", false],
  // Additional Configurations
  annualVehiclesMiles: ["number", false],
  avgAtHomeRate: ["number", false],
  avgDCFCRate: ["number", false],
  benefitToBuilding: ["number", false],
  benefitToResident: ["number", false],
  discountRate: ["number", false],
  evEfficiency: ["number", false],
  roiPeriod: ["number", false],
  vehiclesPerPrivateSpot: ["number", false],
  vehiclesPerSharedSpot: ["number", false],
  rowid: ["number", false],
  sessionId: ["string", false],
  timestamp: ["string", false],
  incentives: ["object", false],
  roiMonthlyArr: ["object", false],
  roiAnnualArr: ["object", false],
  costMonthlyArr: ["object", false],
  costOneTimeArr: ["object", false]
}

const initAdvancedConfigsToShow = (state) => {
  return [
    {
      id: 'annualVehiclesMiles',
      placeholder: 'Annual Vehicle Miles',
      defaultValue: state.value.annualVehiclesMiles,
      units: 'Miles',
      range: [2000, 25000],
      allowDecimals: false,
      text: 'The number of miles travelled per vehicle in the state. This estimate is sourced from the <a href="http://www.fhwa.dot.gov/" target ="_blank">U.S. Federal Highway Administration. </a>'
    },
    {
      id: 'avgAtHomeRate',
      placeholder: 'Average Commercial Rate',
      defaultValue: state.value.avgAtHomeRate,
      units: '$/kWh',
      range: [0.05, 0.8],
      allowDecimals: true,
      text: 'The average price of electricity for commercial customers in the selected state. This estimate includes fees such as demand charges and is sourced from the <a href="http://www.eia.gov" target ="_blank">U.S. Energy Information Administration.</a> The commercial rate is used because chargers are typically connected to panels with commercial rates.'
    },
    {
      id: 'avgDCFCRate',
      placeholder: 'Average DC Fast Charger Rate',
      defaultValue: state.value.avgDCFCRate,
      units: '$/kWh',
      range: [0.05, 1.5],
      allowDecimals: true,
      text: 'The average price of fast charging in the selected state. It serves as the upper bound for the rate to be charged to residents in this scenario estimate. For more information on fast charging rates, please see <a href="https://stable.auto/insights/electric-vehicle-charger-price-by-state" target ="_blank"> Stable Auto.</a>' 
    },
    {
      id: 'discountRate',
      placeholder: 'Discount Rate',
      defaultValue: state.value.discountRate,
      units: '',
      range: [0.01, 0.3],
      allowDecimals: true,
      text: 'The rate at which to discount future cash flows. Private companies typically utilize the weighted average cost of capital (WACC) for this variable, while state and federal agencies may find using the 10-year treasury rate to be more appropriate.'
    },
    {
      id: 'evEfficiency',
      placeholder: 'EV Efficiency',
      defaultValue: state.value.evEfficiency,
      units: 'kWh/mile',
      range: [0.1, 2],
      allowDecimals: true,
      text: 'The average energy needed for an average EV to drive one mile.'
    },
    {
      id: 'roiPeriod',
      placeholder: 'ROI Period',
      defaultValue: state.value.roiPeriod,
      units: 'Years',
      range: [2, 20],
      allowDecimals: false,
      text: 'The number of years over which to model recurring revenue and calculate return on investment (ROI) metrics.'
    },
    {
      id: 'vehiclesPerPrivateSpot',
      placeholder: 'Vehicles Per Private Space',
      defaultValue: state.value.vehiclesPerPrivateSpot,
      units: 'Vehicles',
      range: [1, 4],
      allowDecimals: false,
      text: 'The number of vehicles that are expected to charge per private (i.e., assigned) parking space that has a dedicated charger. (This number may be greater than one if a unit has multiple EVs.)'
    },
    {
      id: 'vehiclesPerSharedSpot',
      placeholder: 'Vehicles Per First Come, First Served Space',
      defaultValue: state.value.vehiclesPerSharedSpot,
      units: 'Vehicles',
      range: [1, 12],
      allowDecimals: false,
      text: 'The number of vehicles that are expected to charge per first come, first served (i.e., unassigned) charger. (Typically, people have to move their cars after charging in one of these spaces.)'
    }
  ]
}

const validateInputs = (state) => {
    // if (state.value.parkingSpacesToElectrify === null || state.value.parkingSpaces === null) {
    //   state.value = {...state.value, 
    //     parkingSpacesToElectrify: state.value.parkingSpaces ? Math.ceil((state.value.parkingSpaces/10)) : null,
    //     assignedParkingSpacesToElectrify: state.value.assignedParkingSpaces ? Math.ceil((state.value.assignedParkingSpaces/10)): null
    //   };
    // }
    for (const [key, value] of Object.entries(state.value)) {
      if (inputTypesRequired[key][1] && (value === null || value === '')) {
        console.log(key, value)
        state.valid = false;
        return;
      }
      if (!(typeof value === inputTypesRequired[key][0] || !value)) {
        console.log(key, value)
        state.valid = false;
        return;
      }
      if (key === 'yearBuilt' && (value < 1000 || value > 3000)) {
        console.log(key, value)
        state.valid = false;
        return;
      }
      if (inputTypesRequired[key][0] === 'number' && value < 0) {
        console.log(key, value)
        state.valid = false;
        return;
      }
      if (key === 'role' && value.includes("Other") && (state.value.customRole === null || state.value.customRole === '')) {
        console.log(key, value)
        state.valid = false;
        return;
      }
      if (key === 'optIn' && value === true && state.value.emailAddress === null) {
        console.log(key, value)
        state.valid = false;
        return;
      }
      if (key === 'emailAddress' && value !== null && !value.includes('@')) {
        console.log(key, value)
        state.valid = false;
        return;
      }
    }
    if (state.value.parkingSpacesToElectrify && state.value.parkingSpacesToElectrify  > state.value.parkingSpaces) {
      state.valid = false;
      return;
    }
    if (state.value.assignedParkingSpacesToElectrify && state.value.assignedParkingSpacesToElectrify  > state.value.assignedParkingSpaces) {
      state.valid = false;
      return;
    }
    if (!validateAdvancedConfigs(state)) {
      state.valid = false;
      return;
    }
    state.valid = true;
  }

const validateAdvancedConfigs = (state) => {
  for (const [key, config] of Object.entries(state.advancedConfigToShow)) {
    if (!(state.value[config.id] === null) && ((state.value[config.id] < config.range[0]) || (state.value[config.id] > config.range[1]))) {
      return false;
    }
  }
  return true;
}

export const inputs = createSlice({
  name: 'inputs',
  initialState: initialState,
  reducers: {
    populateInputs: (state, action) => {
      state.value = action.payload
      if (state.updateSpacesToElectrify) {
        state.value = {...state.value, parkingSpacesToElectrify: Math.ceil((action.payload.parkingSpaces)/10)};
        state.value = {...state.value, assignedParkingSpacesToElectrify: Math.ceil((action.payload.assignedParkingSpaces)/10)};
      }
      validateInputs(state);
      state.enableRecalculate = true;
      // state.enableReset = true;
      state.advancedConfigToShow = initAdvancedConfigsToShow(state);
      saveState("inputs", state);
    },
    resetEnableRecalculate: (state, action) => {
      state.enableRecalculate = action.payload;
    },
    resetEnableReset: (state, action) => {
      state.enableReset = action.payload;
    },
    clearAdvancedConfigs: (state) => {
      state.value = {...state.value,
      evEfficiency : null,
      discountRate : null,
      roiPeriod : null,
      vehiclesPerPrivateSpot : null,
      vehiclesPerSharedSpot : null,
      annualVehiclesMiles : null,
      avgAtHomeRate : null,
      avgDCFCRate : null,
      benefitToBuilding : null,
      benefitToResident : null,
      incentives : [],
    }},
    allowUpdateElectrify: (state, action) => {
      state.updateSpacesToElectrify = action.payload
    },
    processIncentiveSearch: (state, action) => {
      state.value.incentives = state.value.incentives.filter((incentive) => incentive.applied);
      action.payload = action.payload.filter(incentive => !state.value.incentives.map(incentive => incentive["index"]).includes(incentive["index"]));
      action.payload = action.payload.map(incentive => ({...incentive, applied: false}));
      state.value.incentives = state.value.incentives.concat(action.payload);
    },
    processAddress: (state, action) => {
      console.log(action.payload);
      const findPart = (partTypes) => {
        var location = '';
        for (let i = 0; i < partTypes.length; i++) {
          if (action.payload.address_components.find((component) => component.types.includes(partTypes[i]))) {
            location = action.payload.address_components.find((component) => component.types.includes(partTypes[i]));
          }
        }
        if (location === '') {
          console.log(action.payload.address_components);
        }
        return location === '' ? null : location.long_name ;
      }
      const city = findPart(['locality', 'sublocality']);
      const zip = findPart(['postal_code']);
      const usState = findPart(['administrative_area_level_1']);
      const streetNum = findPart(['street_number']);
      const streetName = findPart(['route']);
      const streetAddress = streetNum && streetName ? action.payload.formatted_address : null;
      state.value = {...state.value, city: city, state: usState, zip: zip, streetAddress: streetAddress};
      validateInputs(state);
      saveState("inputs", state);
    }
  }
})

// Action creators are generated for each case reducer function
export const { populateInputs, allowUpdateElectrify, clearAdvancedConfigs, processIncentiveSearch, processAddress, resetEnableRecalculate, resetEnableReset } = inputs.actions

export default inputs.reducer